import type { ArsColorCode, BarDiagramData } from '../types/barDiagram';

/**
 * Reverse mapping: p-code → estado numérico del TXT.
 * p0→0, p1→1, p2→2, p3→3, pN→N (para N≥4), '-'→0
 */
function pCodeToState(pcode: ArsColorCode): number {
  if (pcode === '-') return 0;
  const num = parseInt(pcode.slice(1), 10);
  return isNaN(num) ? 0 : num;
}

/**
 * Serializa un BarDiagramData editado de vuelta al formato TXT de ARS_93.
 * Requiere que `data.rawTxt` exista (solo funciona con fuentes TXT).
 */
export function serializeToArsTxt(data: BarDiagramData): string {
  const raw = data.rawTxt;
  if (!raw) throw new Error('No hay datos brutos TXT para serializar');

  const { numGrupos, numFases, tipos, desf, desfS0, planArrays } = raw;
  const numSlots = tipos.length;

  // --- Mapa de grupos editados (por índice 0-based) ---
  const editedGroups = new Map<number, typeof data.groups[0]>();
  for (const group of data.groups) {
    editedGroups.set(group.id - 1, group);
  }

  // --- Reconstruir TablaFasesTrans ---
  const slotsOut: string[] = [];
  for (let s = 0; s < numSlots; s++) {
    const states: number[] = [];
    for (let g = 0; g < numGrupos; g++) {
      const edited = editedGroups.get(g);
      if (edited && s < edited.cells.length) {
        states.push(pCodeToState(edited.cells[s]));
      } else {
        // Grupo constante (filtrado) — usar estado original
        const slotData = raw.fullStateMatrix[s];
        states.push(slotData && g < slotData.length ? slotData[g] : 3);
      }
    }
    slotsOut.push(`[${states.join(', ')}]`);
  }

  // --- Reconstruir P (duraciones por plan) ---
  // Plan 0: extraer duraciones de fases F desde data.phases (posiblemente editadas)
  const plan0: number[] = [];
  for (let s = 0; s < numSlots; s++) {
    if (tipos[s] === 'F') {
      plan0.push(data.phases[s]?.duration ?? 0);
    }
  }
  const allPlans: string[] = [];
  allPlans.push(`[${plan0.join(', ')}]`);
  for (let p = 1; p < planArrays.length; p++) {
    allPlans.push(`[${planArrays[p].join(', ')}]`);
  }

  // --- Reconstruir TablaFasesTransTime ---
  const transTimeOut: number[] = [];
  for (let s = 0; s < numSlots; s++) {
    if (tipos[s] === 'F') {
      // Para fases F, preservar valor original del transTimeRaw
      transTimeOut.push(raw.transTimeRaw[s] ?? 0);
    } else {
      // Para transiciones, usar duración posiblemente editada
      transTimeOut.push(data.phases[s]?.duration ?? 0);
    }
  }

  // --- Construir líneas de salida ---
  const lines: string[] = [
    `F: ${raw.filename}`,
    `Desf: ${desf.join(', ')}`,
    `P: ${allPlans.join(', ')}`,
    `TablaFasesTransTime: ${transTimeOut.join(', ')}`,
    `TablaFasesTrans: ${slotsOut.join(', ')}`,
    `TablaFasesTransTipo: ${tipos.join(', ')}`,
    `NumGrupos: ${numGrupos}`,
    `NumFases: ${numFases}`,
    `TablaTransicionesT: ${raw.transicionesTRaw}`,
    `TablaTransicionesC: ${raw.transicionesCRaw}`,
    `INC: ${raw.incRaw}`,
    `Desf_S0: ${desfS0.join(', ')}`,
  ];

  return lines.join('\n') + '\n';
}
